import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { api } from "@/lib/api";
import { money } from "@/lib/helpers";
import { useAuth } from "@/context/AuthContext";

const COLORS_BAR = "#F7941D";
const COLORS_PIE = ["#F7941D", "#29ABE2", "#10b981", "#8b5cf6", "#ef4444", "#eab308", "#14b8a6", "#ec4899"];

const ESTADO_CLR = {
  Borrador: "#94a3b8",
  Confirmada: "#29ABE2",
  Facturada: "#F7941D",
  Cobrada: "#10b981",
};

const monthLabel = (ym) => {
  if (!ym || ym.length < 7) return "—";
  const [y, m] = ym.split("-");
  const names = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  return `${names[+m - 1]} ${y.slice(2)}`;
};

export default function Reports() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.http.get("/reports/summary");
        setData(d);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Error cargando informes");
      }
    })();
  }, []);

  const sendMonthlyNow = async () => {
    if (!window.confirm("¿Enviar el informe mensual del mes anterior al correo configurado?")) return;
    setSending(true);
    try {
      const { data: r } = await api.http.post("/reports/send-monthly-now", {});
      toast.success(`Informe enviado a ${r.to}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error al enviar el informe");
    } finally {
      setSending(false);
    }
  };

  const [prlBusy, setPrlBusy] = useState(false);
  const runPrlReminders = async () => {
    if (!window.confirm("¿Escanear caducidades PRL y enviar avisos a los empleados y al admin?")) return;
    setPrlBusy(true);
    try {
      const { data: r } = await api.http.post("/reports/run-prl-reminders", {});
      const c = r.counts;
      toast.success(`Avisos PRL enviados: ${c.sent} · Ya notificados: ${c.skipped} · Errores: ${c.errors}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error en los avisos PRL");
    } finally {
      setPrlBusy(false);
    }
  };

  const [medicoBusy, setMedicoBusy] = useState(false);
  const runMedicoReminders = async () => {
    if (!window.confirm("¿Escanear caducidades de reconocimiento médico y enviar avisos?")) return;
    setMedicoBusy(true);
    try {
      const { data: r } = await api.http.post("/reports/run-medical-reminders", {});
      const c = r.counts;
      toast.success(`Avisos médicos enviados: ${c.sent} · Ya notificados: ${c.skipped} · Errores: ${c.errors}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error en los avisos médicos");
    } finally {
      setMedicoBusy(false);
    }
  };

  if (err) return <div className="card p-6 text-red-600">{err}</div>;
  if (!data) return <div className="text-slate-400 text-sm">Cargando informes...</div>;

  const mensualFmt = data.mensual.map((m) => ({ label: monthLabel(m.mes), total: m.total }));
  const topClientes = data.por_cliente.slice(0, 8);
  const totalCli = topClientes.reduce((s, c) => s + c.total, 0) || 1;

  return (
    <section className="space-y-6" data-testid="reports-root">
      {isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card p-5 flex flex-col gap-3"
               style={{ background: "linear-gradient(135deg,#fff7ed 0%,#eff6ff 100%)" }}>
            <div>
              <div className="font-bold text-sm flex items-center gap-2">
                <svg className="w-5 h-5 text-[var(--cvt-orange)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
                Resumen mensual automático
              </div>
              <div className="text-xs text-slate-600 mt-1">
                Cron día 1 · 08:00 · informe del mes anterior al correo configurado.
              </div>
            </div>
            <button className="btn btn-primary w-full justify-center" onClick={sendMonthlyNow} disabled={sending}
                    data-testid="btn-send-monthly">
              {sending ? "Enviando..." : "Enviar ahora"}
            </button>
          </div>
          <div className="card p-5 flex flex-col gap-3"
               style={{ background: "linear-gradient(135deg,#fef2f2 0%,#fff7ed 100%)" }}>
            <div>
              <div className="font-bold text-sm flex items-center gap-2">
                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 2M12 22a10 10 0 100-20 10 10 0 000 20z"/>
                </svg>
                Avisos PRL automáticos
              </div>
              <div className="text-xs text-slate-600 mt-1">
                Cron diario · 08:15 · avisa al empleado y al admin ≤30 días.
              </div>
            </div>
            <button className="btn btn-primary w-full justify-center" onClick={runPrlReminders} disabled={prlBusy}
                    data-testid="btn-run-prl">
              {prlBusy ? "Escaneando..." : "Escanear ahora"}
            </button>
          </div>
          <div className="card p-5 flex flex-col gap-3"
               style={{ background: "linear-gradient(135deg,#ecfeff 0%,#eff6ff 100%)" }}>
            <div>
              <div className="font-bold text-sm flex items-center gap-2">
                <svg className="w-5 h-5 text-[var(--cvt-blue)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h5M20 20v-5h-5M5 19A9 9 0 0019 5M19 5l-3 3M5 19l3-3"/>
                </svg>
                Avisos reconocimiento médico
              </div>
              <div className="text-xs text-slate-600 mt-1">
                Cron diario · 08:20 · avisa al empleado y al admin ≤30 días.
              </div>
            </div>
            <button className="btn btn-primary w-full justify-center" onClick={runMedicoReminders} disabled={medicoBusy}
                    data-testid="btn-run-medico">
              {medicoBusy ? "Escaneando..." : "Escanear ahora"}
            </button>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-5">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Facturación total</div>
          <div className="text-3xl font-extrabold mt-1 text-[var(--cvt-orange)]">{money(data.total_global)}</div>
          <div className="text-xs text-slate-400 mt-1">Acumulado · todas las ventas</div>
        </div>
        <div className="card p-5">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Nº de ventas</div>
          <div className="text-3xl font-extrabold mt-1 text-[var(--cvt-blue)]">{data.num_ventas}</div>
          <div className="text-xs text-slate-400 mt-1">Registradas en el ERP</div>
        </div>
        <div className="card p-5">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Clientes con facturación</div>
          <div className="text-3xl font-extrabold mt-1 text-emerald-600">{data.por_cliente.length}</div>
          <div className="text-xs text-slate-400 mt-1">Distintos en el periodo</div>
        </div>
      </div>

      {/* Mensual */}
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1.5 h-6 rounded bg-gradient-to-b from-[var(--cvt-orange)] to-[var(--cvt-blue)]"/>
          <h2 className="font-extrabold text-lg tracking-tight">Facturación mensual</h2>
          <span className="text-xs text-slate-500 ml-2">últimos 12 meses</span>
        </div>
        <div className="w-full h-72" data-testid="chart-mensual">
          <ResponsiveContainer>
            <BarChart data={mensualFmt} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }}/>
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? (v/1000).toFixed(0)+"k" : v}/>
              <Tooltip formatter={(v) => money(v)} cursor={{ fill: "rgba(247,148,29,.08)" }}/>
              <Bar dataKey="total" fill={COLORS_BAR} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Por cliente */}
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1.5 h-6 rounded bg-[var(--cvt-blue)]"/>
            <h2 className="font-extrabold text-lg tracking-tight">Facturación por cliente</h2>
          </div>
          {topClientes.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">Sin ventas aún</div>
          ) : (
            <div className="w-full h-72" data-testid="chart-clientes">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={topClientes} dataKey="total" nameKey="cliente"
                       cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2}>
                    {topClientes.map((_, i) => (
                      <Cell key={i} fill={COLORS_PIE[i % COLORS_PIE.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => money(v)} />
                  <Legend verticalAlign="bottom" height={36} iconType="circle"
                          formatter={(val) => <span className="text-xs text-slate-700">{val}</span>}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Por estado */}
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1.5 h-6 rounded bg-[var(--cvt-orange)]"/>
            <h2 className="font-extrabold text-lg tracking-tight">Por estado de venta</h2>
          </div>
          {data.por_estado.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">Sin ventas aún</div>
          ) : (
            <div className="space-y-3" data-testid="chart-estados">
              {data.por_estado.map((e) => {
                const pct = data.total_global > 0 ? Math.round((e.total / data.total_global) * 100) : 0;
                const color = ESTADO_CLR[e.estado] || "#64748b";
                return (
                  <div key={e.estado}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <div className="font-semibold flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: color }}/>
                        {e.estado}
                        <span className="text-xs text-slate-500 font-normal">· {e.count} venta(s)</span>
                      </div>
                      <div className="font-bold">{money(e.total)}</div>
                    </div>
                    <div className="progress-track">
                      <div className="h-full transition-all"
                           style={{ width: `${pct}%`, background: color }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
